import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faExchangeAlt,
  faUser,
  faSpinner,
  faBolt,
  faFilter,
  faSearch,
  faDownload,
  faChevronDown,
  faChevronRight,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import type { CaseAuditEntry } from '../../../types/caseFile';
import api from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import s from '../CaseFilePage.module.css';

interface CaseAuditModalProps {
  caseRef: string;
  entries: CaseAuditEntry[];
  isLoading?: boolean;
  onClose: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

function formatMetadata(metadata: Record<string, unknown> | null | undefined): React.ReactNode {
  if (!metadata || Object.keys(metadata).length === 0) return '-';

  return <pre className={s.auditJson}>{JSON.stringify(metadata, null, 2)}</pre>;
}

export default function CaseAuditModal({
  caseRef,
  entries,
  isLoading,
  onClose,
  onLoadMore,
  hasMore,
}: CaseAuditModalProps) {
  const [typeFilter, setTypeFilter] = useState<'all' | 'lifecycle' | 'field_change' | 'activity'>(
    'all',
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { isSuperAdmin, hasPermission } = usePermissions();
  const canExport = isSuperAdmin || hasPermission('export_data');

  const toggleRow = (rowId: string) => {
    const next = new Set(expandedRows);
    if (next.has(rowId)) next.delete(rowId);
    else next.add(rowId);
    setExpandedRows(next);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Pass the current filters to the export endpoint as well
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);

      const res = await api.get(`/api/admin/audit/case/${caseRef}/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `case_audit_${caseRef}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download audit log', err);
      alert('Failed to download audit log. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const matchesType = typeFilter === 'all' || entry.log_type === typeFilter;

      const matchesSearch =
        !searchTerm ||
        entry.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (entry.username && entry.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (entry.field_key && entry.field_key.toLowerCase().includes(searchTerm.toLowerCase())) ||
        JSON.stringify(entry.metadata || {})
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const entryDate = new Date(entry.ts).toISOString().slice(0, 10);
      const matchesFrom = !fromDate || entryDate >= fromDate;
      const matchesTo = !toDate || entryDate <= toDate;

      return matchesType && matchesSearch && matchesFrom && matchesTo;
    });
  }, [entries, typeFilter, searchTerm, fromDate, toDate]);

  return (
    <ModalOverlay onClose={onClose} title={`Case Audit - ${caseRef}`} wide>
      <div className={s.auditFilters}>
        <div className={s.auditFilterGroup} style={{ flex: 2 }}>
          <div className={s.auditSearchBox}>
            <FontAwesomeIcon icon={faSearch} className={s.auditSearchIcon} />
            <input
              type="text"
              placeholder="Search audit events, users, metadata..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={s.auditSearchInput}
            />
          </div>
        </div>

        <div className={s.auditFilterGroup}>
          <FontAwesomeIcon icon={faCalendarAlt} className={s.auditFilterIcon} />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className={s.auditDateInput}
            title="From Date"
          />
          <span className={s.auditFilterDivider}>to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className={s.auditDateInput}
            title="To Date"
          />
        </div>

        <div className={s.auditFilterGroup}>
          <FontAwesomeIcon icon={faFilter} className={s.auditFilterIcon} />
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as 'all' | 'lifecycle' | 'field_change' | 'activity')
            }
            className={s.auditTypeSelect}
          >
            <option value="all">All Types</option>
            <option value="lifecycle">Lifecycle</option>
            <option value="field_change">Field Changes</option>
            <option value="activity">Activity</option>
          </select>
        </div>

        {canExport && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className={s.auditDownloadBtn}
            title="Export full case history to CSV"
          >
            <FontAwesomeIcon icon={isDownloading ? faSpinner : faDownload} spin={isDownloading} />
            {isDownloading ? 'Downloading...' : 'Export'}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className={s.emptyMsg}>
          <FontAwesomeIcon icon={faSpinner} spin />
          &nbsp;Loading audit history…
        </div>
      ) : filteredEntries.length === 0 ? (
        <p className={s.emptyMsg}>
          {searchTerm || typeFilter !== 'all' || fromDate || toDate
            ? 'No entries match your filters.'
            : 'No audit entries found for this case.'}
        </p>
      ) : (
        <div className={s.auditTableWrapper}>
          <table className={s.auditTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Date/Time</th>
                <th>Type</th>
                <th>Event/Field</th>
                <th>Details Preview</th>
                <th>User</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => {
                const rowId = `${entry.log_type}-${entry.id}`;
                const isExpanded = expandedRows.has(rowId);
                const changedAt = new Date(entry.ts);
                const formattedDate = changedAt.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                });
                const formattedTime = changedAt.toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                });

                let badgeClass = s.auditFieldBadge;
                let badgeLabel = 'Field Change';
                let badgeIcon = faExchangeAlt;
                if (entry.log_type === 'lifecycle') {
                  badgeClass = s.auditLifecycleBadge;
                  badgeLabel = 'Lifecycle';
                  badgeIcon = faHistory;
                } else if (entry.log_type === 'activity') {
                  badgeClass = s.auditActivityBadge;
                  badgeLabel = 'Activity';
                  badgeIcon = faBolt;
                }

                return (
                  <React.Fragment key={rowId}>
                    <tr
                      className={isExpanded ? s.auditRowExpanded : ''}
                      onClick={() => toggleRow(rowId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ textAlign: 'center' }}>
                        <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                      </td>
                      <td>
                        <div>{formattedDate}</div>
                        <div className={s.auditTime}>{formattedTime}</div>
                      </td>
                      <td>
                        <span className={badgeClass}>
                          <FontAwesomeIcon icon={badgeIcon} />
                          &nbsp;{badgeLabel}
                        </span>
                      </td>
                      <td>
                        <div className={s.auditLabelText}>{entry.label}</div>
                      </td>
                      <td>
                        <div className={s.auditPreviewText}>
                          {entry.log_type === 'activity'
                            ? (entry.entity_type ? `${entry.entity_type}: ` : '') +
                              (entry.metadata ? JSON.stringify(entry.metadata) : 'Action performed')
                            : `${entry.old_value ?? 'NULL'} → ${entry.new_value ?? 'NULL'}`}
                        </div>
                      </td>
                      <td>
                        <FontAwesomeIcon icon={faUser} />
                        &nbsp;{entry.username || entry.user_id || 'Unknown'}
                      </td>
                      <td>
                        <span className={s.auditSourceTag}>{entry.change_source || 'manual'}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={s.auditDetailRow}>
                        <td colSpan={7}>
                          <div className={s.auditDetailContent}>
                            <div className={s.auditDetailGrid}>
                              <div className={s.auditDetailItem}>
                                <label>Action/Field:</label>
                                <span>{entry.label}</span>
                              </div>
                              {entry.log_type !== 'activity' && (
                                <>
                                  <div className={s.auditDetailItem}>
                                    <label>Old Value:</label>
                                    <span className={s.auditValueWrap}>
                                      {entry.old_value ?? '-'}
                                    </span>
                                  </div>
                                  <div className={s.auditDetailItem}>
                                    <label>New Value:</label>
                                    <span className={s.auditValueWrap}>
                                      {entry.new_value ?? '-'}
                                    </span>
                                  </div>
                                </>
                              )}
                              {entry.log_type === 'activity' && (
                                <>
                                  <div className={s.auditDetailItem}>
                                    <label>Entity Type:</label>
                                    <span>{entry.entity_type || '-'}</span>
                                  </div>
                                  <div className={s.auditDetailItem}>
                                    <label>Entity ID:</label>
                                    <span>{entry.entity_id || '-'}</span>
                                  </div>
                                </>
                              )}
                              <div className={s.auditDetailItem}>
                                <label>Source:</label>
                                <span>{entry.change_source || 'manual'}</span>
                              </div>
                            </div>
                            {entry.log_type === 'activity' && entry.metadata && (
                              <div className={s.auditMetadataSection}>
                                <label>Full Metadata:</label>
                                {formatMetadata(entry.metadata)}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {hasMore && onLoadMore && (
            <div className={s.auditLoadMoreContainer}>
              <button onClick={onLoadMore} disabled={isLoading} className={s.auditLoadMoreBtn}>
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    &nbsp;Loading...
                  </>
                ) : (
                  'Load More History'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </ModalOverlay>
  );
}
